/**
 * RevisionHistory component for viewing file revision history
 */
import React, { useState, useMemo } from 'react';
import {
  History,
  User,
  RotateCcw,
  Clock,
  GitBranch,
  ChevronDown,
  ChevronUp,
  Eye,
  X,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { File, Revision } from '@phrasewright/types';
import { formatDistanceToNow, format } from 'date-fns';
import { DiffViewer } from './DiffViewer';

interface RevisionHistoryProps {
  file: File;
  onRestoreRevision?: (revisionId: string, content: string) => Promise<void>;
  onClose?: () => void;
}

interface RevisionWithDiff extends Revision {
  diffStats?: {
    added: number;
    removed: number;
    modified: number;
  };
}

export function RevisionHistory({
  file,
  onRestoreRevision,
  onClose,
}: RevisionHistoryProps) {
  const [selectedRevision, setSelectedRevision] = useState<string | null>(null);
  const [compareRevision, setCompareRevision] = useState<string | null>(null);
  const [expandedRevisions, setExpandedRevisions] = useState<Set<string>>(
    new Set()
  );
  const [showDiff, setShowDiff] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  // Sort revisions by timestamp (newest first)
  const sortedRevisions = useMemo(() => {
    return [...file.revisions].sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [file.revisions]);

  // Calculate diff stats for each revision
  const revisionsWithDiff = useMemo<RevisionWithDiff[]>(() => {
    return sortedRevisions.map((revision, index) => {
      if (index === sortedRevisions.length - 1) {
        // Last revision (oldest) - compare with empty content
        return {
          ...revision,
          diffStats: {
            added: revision.content.split('\n').length,
            removed: 0,
            modified: 0,
          },
        };
      }

      const previousRevision = sortedRevisions[index + 1];
      const currentLines = revision.content.split('\n');
      const previousLines = previousRevision.content.split('\n');

      // Simple diff calculation
      const maxLines = Math.max(currentLines.length, previousLines.length);
      let added = 0,
        removed = 0,
        modified = 0;

      for (let i = 0; i < maxLines; i++) {
        const current = currentLines[i];
        const previous = previousLines[i];

        if (current === undefined) {
          removed++;
        } else if (previous === undefined) {
          added++;
        } else if (current !== previous) {
          modified++;
        }
      }

      return {
        ...revision,
        diffStats: { added, removed, modified },
      };
    });
  }, [sortedRevisions]);

  const handleRevisionSelect = (revisionId: string) => {
    if (selectedRevision === revisionId) {
      setSelectedRevision(null);
    } else {
      setSelectedRevision(revisionId);
    }
  };

  const handleExpandRevision = (revisionId: string) => {
    const newExpanded = new Set(expandedRevisions);
    if (newExpanded.has(revisionId)) {
      newExpanded.delete(revisionId);
    } else {
      newExpanded.add(revisionId);
    }
    setExpandedRevisions(newExpanded);
  };

  const handleRestoreRevision = async (revision: Revision) => {
    if (!onRestoreRevision) return;

    try {
      setIsRestoring(true);
      await onRestoreRevision(revision.id, revision.content);
    } catch (error) {
    } finally {
      setIsRestoring(false);
    }
  };

  const handleCompareRevisions = () => {
    if (!selectedRevision) return;

    // Find the previous revision for comparison
    const currentIndex = revisionsWithDiff.findIndex(
      (r) => r.id === selectedRevision
    );
    if (currentIndex < revisionsWithDiff.length - 1) {
      setCompareRevision(revisionsWithDiff[currentIndex + 1].id);
    }
    setShowDiff(true);
  };

  const getChangesBadgeColor = (stats?: {
    added: number;
    removed: number;
    modified: number;
  }) => {
    if (!stats) return 'secondary';
    const totalChanges = stats.added + stats.removed + stats.modified;
    if (totalChanges > 20) return 'destructive';
    if (totalChanges > 5) return 'default';
    return 'secondary';
  };

  const getChangesSummary = (stats?: {
    added: number;
    removed: number;
    modified: number;
  }) => {
    if (!stats) return 'No changes';
    const { added, removed, modified } = stats;
    const parts = [];

    if (added > 0) parts.push(`+${added}`);
    if (removed > 0) parts.push(`-${removed}`);
    if (modified > 0) parts.push(`~${modified}`);

    return parts.length > 0 ? parts.join(' ') : 'No changes';
  };

  if (revisionsWithDiff.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <History className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium text-foreground">
          No Revision History
        </h3>
        <p className="text-sm text-muted-foreground mt-2">
          This file has no revision history yet. Changes will be tracked
          automatically.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-3">
          <History className="h-5 w-5" />
          <div>
            <h2 className="font-semibold">Revision History</h2>
            <p className="text-sm text-muted-foreground">
              {file.name} • {revisionsWithDiff.length} revision
              {revisionsWithDiff.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selectedRevision && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCompareRevisions}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Compare
                  </Button>
                </TooltipTrigger>
                <TooltipContent>View diff for selected revision</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {onClose && (
            <Button size="sm" variant="ghost" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Revision List */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {revisionsWithDiff.map((revision, index) => {
            const isSelected = selectedRevision === revision.id;
            const isExpanded = expandedRevisions.has(revision.id);
            const isCurrent = index === 0;
            const relativeTime = formatDistanceToNow(
              new Date(revision.timestamp),
              {
                addSuffix: true,
              }
            );
            const absoluteTime = format(new Date(revision.timestamp), 'PPpp');

            return (
              <Card
                key={revision.id}
                className={`cursor-pointer transition-all ${
                  isSelected ? 'ring-2 ring-primary' : 'hover:border-primary/50'
                }`}
              >
                <CardContent className="p-4">
                  {/* Revision Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className="flex items-center gap-2 cursor-pointer"
                        onClick={() => handleRevisionSelect(revision.id)}
                      >
                        <GitBranch className="h-4 w-4 text-muted-foreground" />
                        <span className="font-mono text-sm text-muted-foreground">
                          {revision.id.substring(0, 8)}
                        </span>
                        {isCurrent && (
                          <Badge variant="default" className="text-xs">
                            Current
                          </Badge>
                        )}
                      </div>

                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <span>{relativeTime}</span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{absoluteTime}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>

                      {revision.author && (
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <User className="h-3 w-3" />
                          <span>{revision.author}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Changes Badge */}
                      <Badge variant={getChangesBadgeColor(revision.diffStats)}>
                        {getChangesSummary(revision.diffStats)}
                      </Badge>

                      {/* Size */}
                      <span className="text-xs text-muted-foreground">
                        {((revision.size || 0) / 1024).toFixed(1)} KB
                      </span>

                      {/* Expand/Collapse Button */}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleExpandRevision(revision.id)}
                        className="h-6 w-6 p-0"
                      >
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Revision Message */}
                  {revision.message && (
                    <p className="text-sm mt-2 text-foreground">
                      {revision.message}
                    </p>
                  )}

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="space-y-3">
                        {/* Metadata */}
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">
                              Revision ID:
                            </span>
                            <p className="font-mono">{revision.id}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">
                              File Path:
                            </span>
                            <p className="font-mono">{revision.filePath}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Size:</span>
                            <p>{(revision.size || 0).toLocaleString()} bytes</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">
                              Status:
                            </span>
                            <p>{revision.status}</p>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 pt-2">
                          {!isCurrent && onRestoreRevision && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      handleRestoreRevision(revision)
                                    }
                                    disabled={isRestoring}
                                  >
                                    {isRestoring ? (
                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                      <RotateCcw className="h-4 w-4 mr-2" />
                                    )}
                                    Restore
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Restore file to this revision</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </ScrollArea>

      {/* Diff Dialog */}
      {showDiff && selectedRevision && compareRevision && (
        <Dialog open={showDiff} onOpenChange={setShowDiff}>
          <DialogContent className="max-w-6xl max-h-[80vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Revision Comparison</DialogTitle>
            </DialogHeader>
            <DiffViewer
              oldContent={
                revisionsWithDiff.find((r) => r.id === compareRevision)
                  ?.content || ''
              }
              newContent={
                revisionsWithDiff.find((r) => r.id === selectedRevision)
                  ?.content || ''
              }
              oldTitle={`Revision ${compareRevision?.substring(0, 8)}`}
              newTitle={`Revision ${selectedRevision.substring(0, 8)}`}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// Wrapper component for dialog usage
export function RevisionHistoryDialog({
  children,
  file,
  onRestoreRevision,
}: {
  children: React.ReactNode;
  file: File;
  onRestoreRevision?: (revisionId: string, content: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="sr-only">Revision History</DialogTitle>
        </DialogHeader>
        <RevisionHistory
          file={file}
          onRestoreRevision={onRestoreRevision}
          onClose={() => setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
